import { useState, useEffect } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { Brain, Sparkles, ShieldAlert, Trophy, ChevronRight, RefreshCw, Swords, History, TrendingUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import AIWalletTopupModal from "./AIWalletTopupModal";

const LINE_COLOR = {
  GK: "from-amber-400 to-amber-600 border-amber-400/50",
  D: "from-emerald-500 to-emerald-700 border-emerald-500/50",
  M: "from-blue-500 to-blue-700 border-blue-500/50",
  A: "from-rose-500 to-rose-700 border-rose-500/50",
};

export default function LeagueAICoach() {
  const { league, teams, myTeam, isAdmin, wallet, reloadWallet } = useLeague();
  const [teamId, setTeamId] = useState(myTeam?.team_id || teams[0]?.team_id || "");
  const [analysis, setAnalysis] = useState(null);
  const [aiText, setAiText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [openModule, setOpenModule] = useState(null);
  const [showTopup, setShowTopup] = useState(false);

  const run = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const r = await api.get(`/leagues/${league.league_id}/teams/${teamId}/mantra-analysis`);
      setAnalysis(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const askAI = async () => {
    if (!teamId) return;
    setAiLoading(true); setAiText(null);
    try {
      const r = await api.post(`/leagues/${league.league_id}/teams/${teamId}/ai-coach`);
      setAnalysis(r.data.analysis);
      setAiText(r.data.ai_opinion);
      toast.success("🧠 AI Coach ha risposto");
      reloadWallet?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "ai_wallet_empty" || e?.response?.status === 402) {
        setShowTopup(true);
        toast.error("Wallet AI esaurito. Scegli un pacchetto per continuare.");
        reloadWallet?.();
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setAiLoading(false); }
  };

  const teamName = teams.find(t => t.team_id === teamId)?.name || "";

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold flex items-center gap-2">
            <Brain size={14}/> AI Coach Mantra
          </div>
          <h2 className="text-3xl font-black tracking-tight">Analisi tattica rosa</h2>
          <p className="text-slate-400 text-sm mt-1">Copertura degli 11 moduli Mantra 2025/26, sofferenze per ruolo, pareri AI con Claude Sonnet 5.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {(isAdmin || !myTeam) && (
            <select value={teamId} onChange={e=>{ setTeamId(e.target.value); setAnalysis(null); setAiText(null); }}
              className="bg-slate-900 border border-slate-800 rounded-2xl px-3 py-2 text-sm shadow-inner"
              data-testid="ai-team-filter">
              {teams.map(t => <option key={t.team_id} value={t.team_id}>⚽ {t.name}</option>)}
            </select>
          )}
          <button onClick={run} disabled={loading} data-testid="ai-analyze-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 font-bold uppercase text-xs">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Analizza
          </button>
          {(() => {
            const empty = wallet && !wallet.unlimited && wallet.available === 0;
            const btnLabel = aiLoading ? "Chiedo a Claude…"
              : empty ? "Wallet vuoto — Ricarica"
              : "Chiedi ad AI";
            return (
              <button
                onClick={() => empty ? setShowTopup(true) : askAI()}
                disabled={aiLoading}
                data-testid="ai-ask-btn"
                title={empty
                  ? "Ricarica il wallet per continuare"
                  : wallet && !wallet.unlimited
                    ? `${wallet.free_left} gratis + ${wallet.paid_left} pagate rimanenti`
                    : "AI Coach illimitato per admin"}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-black uppercase text-xs shadow-lg disabled:opacity-50 ${
                  empty
                    ? "bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500/25"
                    : "bg-gradient-to-br from-amber-300 to-amber-500 text-slate-950 shadow-amber-500/40"
                }`}
              >
                <Sparkles size={14}/> {btnLabel}
              </button>
            );
          })()}
          {wallet && !wallet.unlimited && (
            <button
              onClick={() => setShowTopup(true)}
              data-testid="ai-topup-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-bold uppercase"
            >
              <Sparkles size={12}/> Ricarica
            </button>
          )}
        </div>
      </div>

      {/* Wallet status banner */}
      {wallet && !wallet.unlimited && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-3 flex items-center gap-4 text-sm">
          <div className="flex-shrink-0">
            <Sparkles size={20} className={wallet.available > 0 ? "text-amber-400" : "text-rose-400"}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-100">
              {wallet.available > 0
                ? <>Hai <span className="text-amber-300 font-mono">{wallet.available}</span> chiamate AI rimaste</>
                : "Wallet AI esaurito"}
            </div>
            <div className="text-xs text-slate-400">
              <span className="text-emerald-400 font-mono">{wallet.free_left}</span> gratis rimaste su {wallet.free_total} · <span className="text-sky-400 font-mono">{wallet.paid_left}</span> pagate
            </div>
          </div>
          {wallet.available > 0 && wallet.available <= 1 && (
            <div className="text-xs text-amber-400 font-bold uppercase tracking-widest hidden sm:block">
              Ultima chiamata!
            </div>
          )}
        </div>
      )}

      {showTopup && (
        <AIWalletTopupModal
          league={league}
          onClose={() => setShowTopup(false)}
          packs={wallet?.packs}
        />
      )}

      {!analysis && !loading && (
        <div className="border border-dashed border-slate-800 rounded-3xl p-12 text-center text-slate-500">
          <Brain size={48} className="mx-auto mb-3 text-purple-400/60"/>
          Seleziona una squadra e clicca <strong className="text-amber-300">Analizza</strong> per vedere copertura moduli e sofferenze.
        </div>
      )}

      {analysis && (
        <>
          {/* Stats card */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatBig emoji="⚽" label="Giocatori" v={analysis.stats.n_giocatori}/>
            <StatBig emoji="💎" label="Qt.A media" v={analysis.stats.avg_quotazione}/>
            <StatBig emoji="🎯" label="FantaMedia" v={analysis.stats.avg_fanta_media ?? "—"}/>
            <StatBig emoji="⭐" label="Media Voto" v={analysis.stats.avg_media_voto ?? "—"}/>
          </div>

          {/* AI Opinion */}
          {aiText && (
            <div className="border border-amber-500/40 rounded-3xl bg-gradient-to-br from-amber-500/5 to-slate-950 p-6 shadow-2xl shadow-amber-500/10" data-testid="ai-opinion">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-amber-300"/>
                <span className="text-xs uppercase tracking-widest text-amber-300 font-black">Parere AI · Claude Sonnet 5 · {teamName}</span>
              </div>
              <div className="prose prose-invert prose-sm max-w-none prose-headings:text-amber-200 prose-strong:text-white prose-li:text-slate-300 prose-p:text-slate-300">
                <ReactMarkdown>{aiText}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Best modules */}
          <div>
            <h3 className="font-black uppercase text-sm tracking-wider mb-2">🏆 Top 3 moduli per la tua rosa</h3>
            <div className="grid md:grid-cols-3 gap-3">
              {analysis.best_modules.map(m => (
                <button key={m.module} onClick={()=>setOpenModule(m.module)}
                  data-testid={`module-card-${m.module}`}
                  className="text-left border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-4 hover:border-amber-400/60 hover:-translate-y-1 transition-all shadow-xl shadow-black/40">
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-black">{m.module}</div>
                    <div className={`text-2xl font-black ${m.score >= 90 ? "text-emerald-400" : m.score >= 70 ? "text-amber-400" : "text-rose-400"}`}>{m.score}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Score / 100</div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mt-2">
                    <div className={`h-full ${m.score >= 90 ? "bg-emerald-400" : m.score >= 70 ? "bg-amber-400" : "bg-rose-400"}`} style={{width: `${m.score}%`}}/>
                  </div>
                  <div className="text-xs text-slate-400 mt-2 flex items-center justify-between">
                    <span>coperti {m.covered}/{m.total}</span>
                    <ChevronRight size={14}/>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Critical roles */}
          {analysis.critical_mantra_roles?.length > 0 && (
            <div className="border border-rose-500/40 rounded-3xl bg-gradient-to-br from-rose-500/5 to-slate-950 p-5 shadow-xl shadow-black/40" data-testid="critical-roles">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert size={16} className="text-rose-400"/>
                <h3 className="font-black uppercase text-sm tracking-wider text-rose-300">Ruoli Mantra in sofferenza (&lt; 2 giocatori)</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {analysis.critical_mantra_roles.map(r => (
                  <span key={r.role} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 text-xs font-black">
                    {r.role} · <span className="text-slate-400 font-normal">{r.count} in rosa</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Module detail modal */}
          {openModule && analysis.by_module[openModule] && (
            <ModuleDetail
              name={openModule}
              data={analysis.by_module[openModule]}
              onClose={()=>setOpenModule(null)}
            />
          )}

          {/* Confronto avversari */}
          <ComparisonSection leagueId={league.league_id} currentTeamId={teamId}/>

          {/* Storico pareri AI */}
          <HistorySection leagueId={league.league_id} teamId={teamId}/>
        </>
      )}
    </div>
  );
}

function ComparisonSection({ leagueId, currentTeamId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leagues/${leagueId}/mantra-comparison`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const toggle = () => {
    if (!open && !data) load();
    setOpen(!open);
  };

  const rankColor = (i) => i === 0 ? "text-emerald-300" : i === 1 ? "text-amber-300" : i === 2 ? "text-orange-300" : "text-slate-400";

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 shadow-xl shadow-black/40" data-testid="comparison-section">
      <button onClick={toggle} className="w-full p-4 flex items-center justify-between hover:bg-slate-800/30 transition-colors" data-testid="comparison-toggle">
        <div className="flex items-center gap-2">
          <Swords size={16} className="text-purple-300"/>
          <h3 className="font-black uppercase text-sm tracking-wider">🆚 Confronto con avversari</h3>
        </div>
        <ChevronRight size={16} className={`transition-transform ${open ? "rotate-90" : ""}`}/>
      </button>
      {open && (
        <div className="border-t border-slate-800 p-4">
          {loading ? <div className="text-center text-slate-500 py-6">Analizzo tutte le squadre…</div> :
          !data ? <div className="text-slate-500 text-center py-6">Nessun dato</div> :
          (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Squadra</th>
                    <th className="py-2 pr-3 text-right">Score</th>
                    <th className="py-2 pr-3">Modulo top</th>
                    <th className="py-2 pr-3 text-right">Qt.A media</th>
                    <th className="py-2 pr-3 text-right">Sofferenze</th>
                    <th className="py-2 pr-3">Top 3 moduli</th>
                  </tr>
                </thead>
                <tbody>
                  {data.teams.map((t, i) => {
                    const isMe = t.team_id === currentTeamId;
                    return (
                      <tr key={t.team_id}
                        className={`border-b border-slate-800/50 ${isMe ? "bg-amber-500/10 border-l-4 border-l-amber-400" : "hover:bg-slate-800/30"} transition-colors`}
                        data-testid={`comparison-row-${t.team_id}`}>
                        <td className={`py-2.5 pr-3 font-black ${rankColor(i)}`}>{i+1}°</td>
                        <td className="py-2.5 pr-3 font-bold">
                          {isMe && <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-2 animate-pulse-slow"/>}
                          {t.team_name}
                        </td>
                        <td className={`py-2.5 pr-3 text-right font-mono font-black ${t.best_score >= 90 ? "text-emerald-400" : t.best_score >= 70 ? "text-amber-400" : "text-rose-400"}`}>
                          {t.best_score}
                        </td>
                        <td className="py-2.5 pr-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-xs font-mono font-black">{t.best_module}</span>
                        </td>
                        <td className="py-2.5 pr-3 text-right font-mono text-slate-300">{t.avg_qta}</td>
                        <td className="py-2.5 pr-3 text-right">
                          <span className={`text-xs font-mono font-black px-2 py-0.5 rounded-full ${t.critical_count === 0 ? "bg-emerald-500/20 text-emerald-300" : t.critical_count <= 3 ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300"}`}>
                            {t.critical_count}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3">
                          <div className="flex gap-1">
                            {t.top3.slice(0, 3).map((m, mi) => (
                              <span key={mi} className="text-[9px] font-mono font-black px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400">{m.m}·{Math.round(m.s)}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistorySection({ leagueId, teamId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leagues/${leagueId}/teams/${teamId}/ai-opinions`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [teamId, open]);

  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 shadow-xl shadow-black/40" data-testid="history-section">
      <button onClick={()=>setOpen(!open)} className="w-full p-4 flex items-center justify-between hover:bg-slate-800/30 transition-colors" data-testid="history-toggle">
        <div className="flex items-center gap-2">
          <History size={16} className="text-blue-300"/>
          <h3 className="font-black uppercase text-sm tracking-wider">📚 Storico pareri AI</h3>
          {items.length > 0 && <span className="text-[10px] text-slate-500 uppercase tracking-widest">{items.length} salvati</span>}
        </div>
        <ChevronRight size={16} className={`transition-transform ${open ? "rotate-90" : ""}`}/>
      </button>
      {open && (
        <div className="border-t border-slate-800 p-4 space-y-3">
          {loading ? <div className="text-center text-slate-500 py-6">Caricamento…</div> :
          items.length === 0 ? (
            <div className="text-center text-slate-500 py-8 text-sm">
              📭 Nessun parere salvato. Ogni volta che cliccherai <strong className="text-amber-300">Chiedi ad AI</strong> il parere verrà archiviato qui per rivederlo in futuro.
            </div>
          ) : (
            <>
              {items.length >= 2 && (
                <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-4" data-testid="history-trend-chart">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp size={14} className="text-blue-300"/>
                    <span className="text-[10px] uppercase tracking-widest text-blue-300 font-black">Trend Top Modulo</span>
                  </div>
                  <div className="h-40">
                    <ResponsiveContainer>
                      <LineChart data={[...items].reverse().map((op, i) => ({
                        idx: i+1,
                        score: op.snapshot?.top3?.[0]?.s || 0,
                        module: op.snapshot?.top3?.[0]?.m || "-",
                        date: op.created_at ? new Date(op.created_at).toLocaleDateString("it-IT", {day:"2-digit", month:"short"}) : "",
                        sofferenze: op.snapshot?.critical_roles?.length || 0,
                      }))}>
                        <defs>
                          <linearGradient id="grScore" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#38BDF8" stopOpacity={1}/>
                            <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0.7}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                        <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }}/>
                        <YAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 10 }}/>
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, boxShadow: "0 12px 32px rgba(0,0,0,.55)" }}
                          formatter={(v, k, entry) => k === "score" ? [`${v}/100 (${entry.payload.module})`, "Score"] : [v, k]}/>
                        <Line type="monotone" dataKey="score" stroke="url(#grScore)" strokeWidth={3} dot={{ fill: "#38BDF8", r: 4 }} activeDot={{ r: 6 }} isAnimationActive animationDuration={900}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              <ol className="divide-y divide-slate-800">
              {items.map(op => {
                const isOpen = expandedId === op.opinion_id;
                return (
                  <li key={op.opinion_id} className="py-3" data-testid={`history-item-${op.opinion_id}`}>
                    <button onClick={()=>setExpandedId(isOpen ? null : op.opinion_id)}
                      className="w-full flex items-start justify-between gap-3 text-left hover:bg-slate-800/20 rounded-2xl p-2 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold flex items-center gap-2 flex-wrap">
                          <span>🧠 {fmt(op.created_at)}</span>
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">S{op.season}</span>
                          {op.snapshot?.top3?.[0] && (
                            <span className="text-[9px] font-mono font-black px-1.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-emerald-300">
                              top: {op.snapshot.top3[0].m} ({op.snapshot.top3[0].s})
                            </span>
                          )}
                          {op.snapshot?.critical_roles?.length > 0 && (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40">
                              {op.snapshot.critical_roles.length} sofferenze
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Rosa: {op.snapshot?.n_players || 0} · Qt.A media {op.snapshot?.avg_qta || "—"}
                        </div>
                      </div>
                      <ChevronRight size={14} className={`text-slate-500 transition-transform mt-1 shrink-0 ${isOpen ? "rotate-90" : ""}`}/>
                    </button>
                    {isOpen && (
                      <div className="mt-3 ml-2 pl-4 border-l-2 border-amber-500/30 pr-2" data-testid={`history-detail-${op.opinion_id}`}>
                        <div className="prose prose-invert prose-sm max-w-none prose-headings:text-amber-200 prose-strong:text-white prose-li:text-slate-300 prose-p:text-slate-300">
                          <ReactMarkdown>{op.ai_opinion}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatBig({ emoji, label, v }) {
  return (
    <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-4 shadow-xl shadow-black/40">
      <div className="flex items-center justify-between">
        <span className="text-2xl">{emoji}</span>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      </div>
      <div className="text-2xl font-mono font-black mt-2">{v}</div>
    </div>
  );
}

function ModuleDetail({ name, data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 shadow-2xl shadow-black/80 p-6"
        data-testid={`module-detail-${name}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-400 font-black">Modulo Mantra 2025/26</div>
            <h3 className="text-3xl font-black">{name}</h3>
            <div className="text-sm text-slate-400 mt-1">Score <strong className="text-amber-300">{data.score}/100</strong> · coperti {data.covered_slots}/{data.total_slots}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl px-3">×</button>
        </div>

        <div className="space-y-3">
          {data.slots.map(s => (
            <div key={s.slot_id} className={`rounded-2xl border p-3 ${s.candidates_count === 0 ? "border-rose-500/50 bg-rose-500/10" : s.candidates_count === 1 ? "border-amber-500/40 bg-amber-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${s.line === "GK" ? "bg-amber-400" : s.line === "D" ? "bg-emerald-400" : s.line === "M" ? "bg-blue-400" : "bg-rose-400"}`}/>
                  <span className="font-black text-sm">{s.label}</span>
                  <span className="text-[9px] text-slate-500 font-mono">accetta: {s.accepts.join("/")}</span>
                </div>
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${s.candidates_count === 0 ? "bg-rose-500 text-white" : s.candidates_count === 1 ? "bg-amber-500 text-slate-950" : "bg-emerald-500 text-slate-950"}`}>
                  {s.candidates_count} disponibili
                </span>
              </div>
              {s.top_candidates.length > 0 ? (
                <div className="text-xs text-slate-300 space-y-0.5 mt-1.5">
                  {s.top_candidates.map(c => (
                    <div key={c.player_id} className="flex items-center justify-between">
                      <span>· <strong>{c.name}</strong> <span className="text-slate-500">{c.roles.join("/")}</span></span>
                      <span className="font-mono text-amber-300">Qt.A {c.quotazione}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-rose-300 mt-1">❌ Nessun giocatore in rosa per questo slot</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
